'use client'

import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

interface ScrollableTabRowProps {
  children: React.ReactNode
  className?: string
  contentClassName?: string
  // The edge fade needs to match whatever surface the row actually sits on (a card,
  // a black chart toolbar, ...) — no single value blends everywhere, so each caller
  // supplies its own `from-*` token instead of guessing at a shared default.
  fadeClassName?: string
}

// Horizontal scroll-with-fade-chevrons container, shared by any tab/stat row that can
// overflow its width (chart market stats row, watchlist tab slider, ...). Recomputes
// scroll affordances on every render (cheap DOM reads) so content-length changes are
// picked up without the caller having to pass a dependency list, and a ResizeObserver
// on the scroller itself catches container resizes (e.g. widget resize).
export function ScrollableTabRow({
  children,
  className,
  contentClassName,
  fadeClassName,
}: ScrollableTabRowProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  // Callers whose content reflows on every keystroke (order-form-summary/
  // order-preview-panel show live qty/price text here) re-render this component
  // every keystroke too. The layout effect below deliberately has no dependency
  // array so it re-measures on every one of those renders — but calling setState
  // synchronously, in a useLayoutEffect, unconditionally on every render, is
  // exactly the shape that becomes a genuine "Maximum update depth exceeded":
  // while the content is actively changing width (mid-keystroke), a boundary
  // case in `scrollWidth`/`clientWidth` can measure differently on consecutive
  // layout passes, so `setState` never stops finding something "changed" to
  // recurse on. A same-tick recompute can't tell that apart from real content
  // growth, so defer the measurement to the next animation frame instead of
  // running it inline — that breaks the synchronous effect→setState→effect
  // chain (each frame is a fresh macrotask, not a nested commit), while still
  // re-measuring every render's worth of DOM changes a frame later.
  const rafRef = useRef<number | null>(null)

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const nextCanScrollLeft = el.scrollLeft > 1
    const nextCanScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
    setCanScrollLeft((current) => (current === nextCanScrollLeft ? current : nextCanScrollLeft))
    setCanScrollRight((current) => (current === nextCanScrollRight ? current : nextCanScrollRight))
  }, [])

  const scheduleUpdateScrollState = useCallback(() => {
    if (rafRef.current !== null) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      updateScrollState()
    })
  }, [updateScrollState])

  useLayoutEffect(() => {
    scheduleUpdateScrollState()
  })

  useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    },
    []
  )

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const resizeObserver = new ResizeObserver(scheduleUpdateScrollState)
    resizeObserver.observe(el)
    return () => resizeObserver.disconnect()
  }, [scheduleUpdateScrollState])

  const scrollByStep = (direction: 1 | -1) => {
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' })
  }

  return (
    <div className={cn('relative flex min-w-0 items-stretch', className)}>
      <div
        ref={scrollRef}
        onScroll={updateScrollState}
        className={cn(
          'flex min-w-0 flex-1 items-center overflow-x-auto overflow-y-hidden scroll-smooth [&::-webkit-scrollbar]:hidden',
          contentClassName
        )}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {children}
      </div>
      {canScrollLeft && (
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex w-7 items-center">
          <div
            aria-hidden="true"
            className={cn(
              'absolute inset-0 bg-linear-to-r to-transparent opacity-70',
              fadeClassName
            )}
          />
          <button
            type="button"
            tabIndex={-1}
            onClick={() => scrollByStep(-1)}
            className="text-foreground/60 hover:text-foreground hover:bg-foreground/10 pointer-events-auto relative flex h-full items-center py-0.75 pr-0.75 pl-0.5 transition-colors"
          >
            <ChevronLeft className="size-3.5" />
          </button>
        </div>
      )}
      {canScrollRight && (
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 flex w-7 items-center justify-end">
          <div
            aria-hidden="true"
            className={cn(
              'absolute inset-0 bg-linear-to-l to-transparent opacity-70',
              fadeClassName
            )}
          />
          <button
            type="button"
            tabIndex={-1}
            onClick={() => scrollByStep(1)}
            className="text-foreground/60 hover:text-foreground hover:bg-foreground/10 pointer-events-auto relative flex h-full items-center py-0.75 pr-0.5 pl-0.75 transition-colors"
          >
            <ChevronRight className="size-3.5" />
          </button>
        </div>
      )}
    </div>
  )
}
